import React, { useEffect, useRef, useState } from 'react';
import GatsbyImage from 'gatsby-image';
import clsx from 'clsx';

import type { ImageWithAlt } from '../../types';
import useKeyPress from '../../hooks/useKeyPress';
import { getImageAlt } from '../../utils/imageAlt';

interface Props {
  className?: string;
  images: Array<ImageWithAlt>;
}

const Gallery: React.FC<Props> = ({ className, images }) => {
  const [clickedFigureIndex, setClickedFigureIndex] = useState<number>(-1);
  const $imageButtons = useRef<Array<HTMLButtonElement | null>>([]);

  useKeyPress('Escape', () => {
    if (images.length && clickedFigureIndex !== -1) {
      closeFigure();
    }
  });

  useKeyPress('ArrowLeft', () => {
    if (images.length && images.length > 1 && clickedFigureIndex !== -1) {
      setClickedFigureIndex(
        (prevState) => (prevState - 1 + images.length) % images.length,
      );
    }
  });

  useKeyPress('ArrowRight', () => {
    if (images.length && images.length > 1 && clickedFigureIndex !== -1) {
      setClickedFigureIndex((prevState) => (prevState + 1) % images.length);
    }
  });

  function closeFigure() {
    setClickedFigureIndex(-1);
  }

  function handleFigureClick(index: number) {
    if (clickedFigureIndex === index) {
      closeFigure();
      return;
    }

    setClickedFigureIndex(index);
  }

  useEffect(() => {
    document.body.classList.toggle('image-zoomed', clickedFigureIndex !== -1);

    return () => {
      document.body.classList.remove('image-zoomed');
    };
  }, [clickedFigureIndex]);

  useEffect(() => {
    if (clickedFigureIndex === -1) return;

    $imageButtons.current[clickedFigureIndex]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;

      event.preventDefault();
      $imageButtons.current[clickedFigureIndex]?.focus();
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [clickedFigureIndex]);

  return (
    <section
      aria-label="Galerija slika"
      className={clsx([className, 'gallery'])}
    >
      {images.map((image, index) => {
        const { asset } = image;
        const imageAlt = getImageAlt(image);
        const thumbnailFluid = {
          ...asset.fluid,
          aspectRatio: 4 / 3,
        };

        return (
          <figure
            aria-label={
              clickedFigureIndex === index ? 'Uvećana slika' : undefined
            }
            aria-modal={clickedFigureIndex === index ? 'true' : undefined}
            className={clsx([clickedFigureIndex === index && 'zoomed'])}
            key={asset._id}
            role={clickedFigureIndex === index ? 'dialog' : undefined}
          >
            <button
              aria-label={
                clickedFigureIndex === index
                  ? `Zatvori sliku: ${imageAlt || `Slika ${index + 1}`}`
                  : `Uvećaj sliku: ${imageAlt || `Slika ${index + 1}`}`
              }
              aria-pressed={clickedFigureIndex === index}
              onClick={() => handleFigureClick(index)}
              ref={($element) => {
                $imageButtons.current[index] = $element;
              }}
              tabIndex={
                clickedFigureIndex === -1 || clickedFigureIndex === index
                  ? 0
                  : -1
              }
              type="button"
            >
              <GatsbyImage
                alt={imageAlt}
                fluid={thumbnailFluid}
                imgStyle={{ objectFit: 'cover' }}
              />
            </button>
          </figure>
        );
      })}
    </section>
  );
};

export default Gallery;
